'use client'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { signOut, useSession } from 'next-auth/react'
import { useProfileImage } from '@/app/ProfileImageContext'
import Logo from '@/public/images/Migration-Law-icon.png'
import MobileMenu from './mobile-menu'
import {
  getBaseNavItems,
  getGuestNavItems,
  desktopGhostButtonClass,
  desktopNavLinkClass,
  desktopPrimaryLinkClass,
} from './navigation'
import LanguageSwitcher from './language-switcher'
import { getLocaleFromPathname, localizeInternalHref } from '@/lib/i18n/paths'
import { getUiDictionary } from '@/lib/i18n/ui'

export default function Header() {
  const { data: session, status } = useSession()
  const { imageUrl } = useProfileImage()
  const userImage = imageUrl || session?.user?.image
  const pathname = usePathname() || '/'
  const locale = getLocaleFromPathname(pathname)
  const t = getUiDictionary(locale)
  const baseNavItems = getBaseNavItems(locale)
  const guestNavItems = getGuestNavItems(locale)
  const homeHref = localizeInternalHref('/', locale)
  const assistantHref = localizeInternalHref('/comprueba-tu-tramite-ia', locale)
  const myCasesHref = localizeInternalHref('/mis-expedientes', locale)
  const profileHref = localizeInternalHref('/perfil', locale)

  return (
    <header className="absolute left-0 top-0 z-30 w-full border-b border-gray-700/70 bg-gray-950/80 shadow-lg backdrop-blur-md">
      <div className="container mx-auto flex h-20 items-center justify-between px-4 sm:px-6">
        <Link href={homeHref} className="flex items-center" aria-label="MigrationLaw">
          <Image
            className="rounded-full transition-transform duration-300 hover:scale-105"
            src={Logo}
            width={72}
            height={72}
            sizes="72px"
            priority
            alt={locale === 'en' ? 'MigrationLaw logo' : 'Logo de MigrationLaw'}
          />
        </Link>

        <nav className="ml-auto hidden items-center gap-2 md:flex" aria-label={locale === 'en' ? 'Main navigation' : 'Navegación principal'}>
          {baseNavItems.map((item) => (
            <Link key={item.href} href={item.href} className={desktopNavLinkClass}>
              {item.label}
            </Link>
          ))}

          <Link href={assistantHref} className={desktopNavLinkClass}>
            {t.nav.aiAssistant}
          </Link>

          {status === 'loading' && (
            <span className="px-3 py-2 text-sm font-medium text-gray-300">{t.nav.loading}</span>
          )}

          {status !== 'loading' && session ? (
            <>
              {session.user?.isAdmin && (
                <Link
                  href="/admin"
                  className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm font-bold text-amber-300 transition-colors hover:bg-amber-500/20"
                >
                  ⚙️ Admin
                </Link>
              )}
              <Link href={myCasesHref} className={desktopNavLinkClass}>
                {t.nav.myCases}
              </Link>
              <Link href={profileHref} className={desktopNavLinkClass}>
                <span className="mr-2">{session.user?.name ?? t.nav.myProfile}</span>
                {typeof userImage === 'string' && (
                  <Image
                    src={userImage}
                    width={28}
                    height={28}
                    sizes="28px"
                    alt="Avatar del usuario"
                    className="rounded-full border border-gray-500"
                  />
                )}
              </Link>
              <button type="button" onClick={() => signOut()} className={desktopGhostButtonClass}>
                {t.nav.signOut}
              </button>
            </>
          ) : null}

          {status !== 'loading' && !session && (
            <>
              <Link href={guestNavItems[0].href} className={desktopNavLinkClass}>
                {guestNavItems[0].label}
              </Link>
              <Link href={guestNavItems[1].href} className={desktopPrimaryLinkClass}>
                {guestNavItems[1].label}
              </Link>
            </>
          )}

          <LanguageSwitcher />
        </nav>

        <MobileMenu
          status={status}
          locale={locale}
          isAuthenticated={Boolean(session)}
          isAdmin={Boolean(session?.user?.isAdmin)}
          userName={session?.user?.name ?? t.nav.myProfile}
          userImage={typeof userImage === 'string' ? userImage : null}
          onSignOut={() => signOut()}
        />
      </div>
    </header>
  )
}
